import React, { useState, useEffect } from 'react'
import PageHeader from '@/components/PageHeader'
import SectionLabel from '@/components/SectionLabel'
import ArchLineBg from '@/components/ArchLineBg'
import { getEquipment } from '@/api/index'

function highlightMatch(text, query) {
  if (!query) return text
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: 'rgba(27,123,63,0.18)', color: 'var(--accent-deep)', borderRadius: 3, padding: '0 2px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  )
}

export default function EquipmentPage() {
  const [search, setSearch] = useState('')
  const [equipmentData, setEquipmentData] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getEquipment()
      .then(data => setEquipmentData(data))
      .catch(() => setEquipmentData([]))
      .finally(() => setLoading(false))
  }, [])

  const totalItems = equipmentData.reduce((s, g) => s + g.items.length, 0)
  const query = search.trim()

  const filtered = equipmentData.map(group => ({
    ...group,
    items: group.items.filter(item =>
      item.description.toLowerCase().includes(query.toLowerCase()) ||
      item.qty.toLowerCase().includes(query.toLowerCase()) ||
      group.title.toLowerCase().includes(query.toLowerCase())
    ),
  })).filter(group => group.items.length > 0)

  const filteredTotal = filtered.reduce((s, g) => s + g.items.length, 0)

  return (
    <div className="page">
      <PageHeader
        eyebrow="Equipment"
        title={<>The <span style={{ color: 'var(--accent)' }}>iron</span> we own.</>}
        sub="An owned, maintained fleet — not a rental list. Every site mobilises from our Baneshwor Height yard with full operator and service backup."
        badge="All equipment owned & operated in-house"
        variant="equipment"
      />

      <section style={{ padding: '64px 0 100px', position: 'relative', overflow: 'hidden' }}>
        <ArchLineBg variant="crane" opacity={0.7} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>

          {/* Header + Search row */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
            <SectionLabel title="List of Equipments" count={`${totalItems} total items`} />

            <div style={{ position: 'relative' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fg-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  paddingLeft: 36, paddingRight: search ? 32 : 14,
                  paddingTop: 9, paddingBottom: 9,
                  border: '1px solid var(--line)', borderRadius: 999,
                  background: '#fff', color: 'var(--fg-0)',
                  fontFamily: 'var(--text)', fontSize: 13, outline: 'none',
                  width: 220, transition: 'border-color .2s, box-shadow .2s',
                }}
                onFocus={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 3px rgba(27,123,63,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--line)'; e.target.style.boxShadow = 'none'; }}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{
                  position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--fg-3)', fontSize: 14, lineHeight: 1, padding: 0,
                }}>✕</button>
              )}
            </div>
          </div>

          {/* Loading skeleton */}
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
              {[1, 2].map(i => (
                <div key={i}>
                  <div style={{ height: 52, background: 'var(--bg-2)', borderRadius: '12px 12px 0 0', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ border: '1px solid var(--line-soft)', borderTop: 'none', borderRadius: '0 0 12px 12px', background: '#fff' }}>
                    {[1,2,3,4,5].map(j => (
                      <div key={j} style={{ height: 50, borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 16 }}>
                        <div style={{ width: 24, height: 12, background: 'var(--bg-2)', borderRadius: 4, flexShrink: 0, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <div style={{ flex: 1, height: 12, background: 'var(--bg-2)', borderRadius: 4, maxWidth: 300, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <div style={{ width: 80, height: 12, background: 'var(--bg-2)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <div style={{ width: 80, height: 24, background: 'var(--bg-2)', borderRadius: 999, animation: 'pulse 1.5s ease-in-out infinite' }} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No results */}
          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--fg-3)' }}>
              <div className="mono" style={{ fontSize: 13 }}>No equipment found for &ldquo;{search}&rdquo;</div>
              <button onClick={() => setSearch('')}
                style={{ marginTop: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 13, fontFamily: 'var(--text)' }}>
                Clear search
              </button>
            </div>
          )}

          {/* Equipment table groups */}
          {!loading && <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
            {filtered.map((group, gi) => (
              <div key={group.category}>
                {/* Category header bar */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '13px 20px',
                  background: 'var(--fg-0)', color: '#fff',
                  borderRadius: '12px 12px 0 0',
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 26, height: 26, borderRadius: '50%',
                    background: 'var(--accent)', color: '#fff',
                    fontFamily: 'var(--space)', fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}>{group.category}</span>
                  <span style={{ fontFamily: 'var(--space)', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
                    {group.title}
                  </span>
                  <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 11, opacity: 0.55 }}>
                    {group.items.length} items
                  </span>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto', border: '1px solid var(--line-soft)', borderTop: 'none', borderRadius: '0 0 12px 12px', background: '#fff' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-1)', borderBottom: '2px solid var(--line-soft)' }}>
                        <th style={{ ...thStyle, width: 56, textAlign: 'center' }}>S.N.</th>
                        <th style={{ ...thStyle }}>Description</th>
                        <th style={{ ...thStyle, width: 140 }}>Quantity</th>
                        <th style={{ ...thStyle, width: 130 }}>Condition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((item, idx) => (
                        <tr key={item.sn}
                          style={{
                            borderBottom: idx < group.items.length - 1 ? '1px solid var(--line-soft)' : 'none',
                            background: idx % 2 === 0 ? '#fff' : 'var(--bg-0)',
                            transition: 'background .15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-soft)'}
                          onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : 'var(--bg-0)'}
                        >
                          <td style={{ ...tdStyle, textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
                            {item.sn}
                          </td>
                          <td style={{ ...tdStyle, fontSize: 14, color: 'var(--fg-0)', fontWeight: 500 }}>
                            {highlightMatch(item.description, query)}
                          </td>
                          <td style={{ ...tdStyle, fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--fg-1)', whiteSpace: 'nowrap' }}>
                            {item.qty}
                          </td>
                          <td style={{ ...tdStyle }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '3px 10px', borderRadius: 999,
                              background: 'rgba(27,123,63,0.10)', color: 'var(--accent-deep)',
                              fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                            }}>
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                              {item.condition}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>}

          {/* Footer count */}
          {!loading && filtered.length > 0 && (
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
              <span className="mono" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                {query
                  ? `${filteredTotal} result${filteredTotal !== 1 ? 's' : ''} for "${search}"`
                  : `${totalItems} items · ${equipmentData.length} categories`}
              </span>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

const thStyle = {
  padding: '11px 16px',
  fontFamily: 'var(--space)',
  fontSize: 11, fontWeight: 700,
  color: 'var(--fg-2)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  textAlign: 'left',
  whiteSpace: 'nowrap',
}

const tdStyle = {
  padding: '12px 16px',
  lineHeight: 1.45,
  verticalAlign: 'middle',
}
